import { useContext, useMemo } from "react";
import { SubscriptionsContext } from "../store/SubscriptionsContext";
import { resolveTierChargeAmountSek } from "../../../utils/tierPricing";

export interface TierWithDisplayName {
  id: string;
  price: number;
  oldCsaMemberPrice: number;
  /** Price this member will be charged (standard or returning). */
  chargePrice: number;
  name: string;
  isAvailable: boolean;
}

type UseTiersWithDisplayNameOptions = {
  hasSubscriptionHistory?: boolean;
};

/**
 * Returns all tiers from all subscriptions, each with a display name
 * in the form "SubscriptionName - TierName (Price)", e.g. "Flowers - Standard (3000)".
 */
export function useTiersWithDisplayName(
  options?: UseTiersWithDisplayNameOptions,
): TierWithDisplayName[] {
  const { subscriptions, tiers: flatTiers } = useContext(SubscriptionsContext);
  const hasSubscriptionHistory = options?.hasSubscriptionHistory ?? false;

  return useMemo(() => {
    return subscriptions.flatMap((subscription) => {
      const rawTiers =
        subscription.tiers && subscription.tiers.length > 0
          ? subscription.tiers
          : flatTiers.filter((t) => t.subscriptionId === subscription.id);
      return rawTiers.map((tier) => ({
        id: tier.id,
        price: tier.price,
        oldCsaMemberPrice: tier.oldCsaMemberPrice,
        chargePrice: resolveTierChargeAmountSek(
          tier,
          hasSubscriptionHistory,
        ),
        name: `${subscription.name} - ${tier.name} (${resolveTierChargeAmountSek(tier, hasSubscriptionHistory)} kr)`,
        isAvailable: tier.isAvailable ?? false,
      }));
    });
  }, [subscriptions, flatTiers, hasSubscriptionHistory]);
}
